import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';

import { api } from './client';
import { planCustomerPolicy, type BillingCustomerType, type PlanPriceUnit } from '@projoint/shared';

// ── Types (mirror backend LimitsService / PlansService) ──────────────────

export interface PlanFeatures {
  github_integration?: boolean;
  custom_roles?: boolean;
  audit_log?: boolean;
  full_reports?: boolean;
  outbound_webhooks?: boolean;
  public_api?: boolean;
  sso?: boolean;
  [key: string]: boolean | undefined;
}

export interface PublicPlan {
  /** Optional while rolling out against an older catalog endpoint. */
  eligibleCustomerTypes?: readonly BillingCustomerType[];
  priceUnit?: PlanPriceUnit;
  code: string;
  name: string;
  monthlyPriceCents: number;
  annualPriceCents: number;
  maxMembers: number | null;
  maxProjects: number | null;
  storageQuotaBytes: number;
  storageQuotaPerSeatBytes: number | null;
  maxFileSizeBytes: number;
  features: PlanFeatures;
  trialDays: number;
  isActive: boolean;
  isPublic: boolean;
  sortOrder: number;
}

export function billingPlanPolicy(plan: PublicPlan) {
  const legacy = planCustomerPolicy(plan.code);
  return { eligibleCustomerTypes: plan.eligibleCustomerTypes ?? legacy.eligibleCustomerTypes,
    priceUnit: plan.priceUnit ?? legacy.priceUnit };
}

export interface EffectiveLimits {
  maxMembers: number | null;
  maxProjects: number | null;
  storageQuotaBytes: number;
  maxFileSizeBytes: number;
  features: PlanFeatures;
  source: {
    maxMembers: 'plan' | 'override';
    maxProjects: 'plan' | 'override';
    storageQuotaBytes: 'plan' | 'override';
    maxFileSizeBytes: 'plan' | 'override';
    features: 'plan' | 'override' | 'merged';
  };
  planCode: string;
}

export interface TeamUsage {
  users: number;
  projects: number;
  storageBytes: number;
  limits: EffectiveLimits;
}

export interface BillingSubscription {
  id: string;
  customerType: 'user' | 'team';
  customerId: string;
  planCode: string;
  status: 'free' | 'trial' | 'active' | 'past_due' | 'cancelled' | 'lifetime';
  paymentProvider: 'manual' | 'stripe' | 'yookassa' | 'lifetime';
  seatCount: number;
  currentPeriodStart: string;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  cancelledAt: string | null;
  grantReason: string | null;
  grantedBy: string | null;
  createdAt: string;
}

export interface UpgradeRequest {
  id: string;
  customerType: 'user' | 'team';
  customerId: string;
  requestedPlanCode: string;
  requestedBy: string;
  note: string | null;
  status: 'pending' | 'granted' | 'declined' | 'cancelled';
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  grantedSubscriptionId: string | null;
  createdAt: string;
}

// ── Query keys + hooks ────────────────────────────────────────────────────

export function plansKey(): QueryKey {
  return ['billing', 'plans'];
}
export function teamUsageKey(teamId: string): QueryKey {
  return ['billing', 'team-usage', teamId];
}
export function personalSubscriptionKey(): QueryKey {
  return ['billing', 'subscription', 'personal'];
}
export function teamSubscriptionKey(teamId: string): QueryKey {
  return ['billing', 'subscription', 'team', teamId];
}
export function onlinePaymentKey(teamId?: string): QueryKey {
  return ['billing', 'online-payment', teamId ?? 'personal'];
}
export function upgradeRequestsKey(customerType: 'user' | 'team', customerId: string): QueryKey {
  return ['billing', 'upgrade-requests', customerType, customerId];
}

export function usePublicPlans() {
  return useQuery({
    queryKey: plansKey(),
    queryFn: () => api<PublicPlan[]>('/api/billing/plans'),
    staleTime: 5 * 60 * 1000, // plans rarely change; cache 5 min
  });
}

/**
 * Live usage snapshot for a team.  Drives FreeTierBanner.
 *
 * Polled every 30s (covers other tabs invalidating the team — e.g. an
 * admin granting subscription).  Caller can manually invalidate via
 * `queryClient.invalidateQueries({ queryKey: teamUsageKey(id) })` after
 * known mutations.
 */
export function useTeamUsage(teamId: string | undefined) {
  return useQuery({
    queryKey: teamId ? teamUsageKey(teamId) : ['billing', 'team-usage', 'noop'],
    queryFn: () => {
      if (!teamId) throw new Error('teamId required');
      return api<TeamUsage>(`/api/billing/teams/${teamId}/usage`);
    },
    enabled: Boolean(teamId),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/** Actor's personal subscription.  Returns null on free tier. */
export function usePersonalSubscription() {
  return useQuery({
    queryKey: personalSubscriptionKey(),
    // Free tier → the API returns an empty 200 body, which the client decodes
    // as `undefined`; coalesce to `null` so react-query doesn't throw
    // "Query data cannot be undefined".
    queryFn: async () =>
      (await api<BillingSubscription | null>('/api/billing/subscription')) ?? null,
    staleTime: 30_000,
  });
}

/** Team's active subscription.  Returns null on free tier. */
export function useTeamSubscription(teamId: string | undefined) {
  return useQuery({
    queryKey: teamId ? teamSubscriptionKey(teamId) : ['billing', 'subscription', 'team', 'noop'],
    queryFn: async () => {
      if (!teamId) throw new Error('teamId required');
      return (
        (await api<BillingSubscription | null>(`/api/billing/teams/${teamId}/subscription`)) ?? null
      );
    },
    enabled: Boolean(teamId),
    staleTime: 30_000,
  });
}

/**
 * Whether self-serve online checkout is enabled (optionally in a team context).
 * Default OFF → the Upgrade CTA offers the in-app upgrade-request flow.
 */
export function useOnlinePayment(teamId?: string) {
  return useQuery({
    queryKey: onlinePaymentKey(teamId),
    queryFn: () => {
      const suffix = teamId ? `?teamId=${teamId}` : '';
      return api<{ enabled: boolean }>(`/api/billing/online-payment${suffix}`);
    },
    staleTime: 60_000,
  });
}

/** The actor's upgrade requests for a customer (personal account or a team). */
export function useUpgradeRequests(customerType: 'user' | 'team', customerId: string | undefined) {
  return useQuery({
    queryKey: customerId
      ? upgradeRequestsKey(customerType, customerId)
      : ['billing', 'upgrade-requests', 'noop'],
    queryFn: () => {
      if (!customerId) throw new Error('customerId required');
      return api<UpgradeRequest[]>(
        `/api/billing/upgrade-requests?customerType=${customerType}&customerId=${customerId}`,
      );
    },
    enabled: Boolean(customerId),
    staleTime: 15_000,
  });
}

/** Raise an in-app upgrade request; invalidates the matching list on success. */
export function useCreateUpgradeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      customerType: 'user' | 'team';
      customerId: string;
      requestedPlanCode: string;
      note?: string;
    }) => api<UpgradeRequest>('/api/billing/upgrade-request', { method: 'POST', body: input }),
    onSuccess: (req) => {
      void qc.invalidateQueries({
        queryKey: upgradeRequestsKey(req.customerType, req.customerId),
      });
    },
  });
}

export interface CheckoutInput {
  customerType: 'user' | 'team';
  customerId: string;
  planCode: string;
  intervalKind?: 'month' | 'year';
  seatCount?: number;
  provider?: 'stripe' | 'yookassa';
  couponCode?: string;
}

/**
 * Start a self-serve checkout — returns the provider's hosted-checkout URL to
 * redirect the browser to.  Only used when `useOnlinePayment().enabled` is true;
 * otherwise the UI uses {@link useCreateUpgradeRequest}.  Returns a 503 (handled
 * by the caller) until the provider's keys are configured.
 */
export function useCreateCheckout() {
  return useMutation({
    mutationFn: (input: CheckoutInput) =>
      api<{ redirectUrl: string; provider: string }>('/api/billing/checkout', {
        method: 'POST',
        body: {
          ...input,
          // The browser returns to whichever billing page started the checkout.
          successUrl: `${window.location.href.split('?')[0]}?checkout=success`,
          cancelUrl: `${window.location.href.split('?')[0]}?checkout=cancelled`,
        },
      }),
  });
}
